import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import './Management.css';

const TeamManagement = () => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', role: '', bio: '', order: '' });
  const [existingImages, setExistingImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [newFilePreviews, setNewFilePreviews] = useState([]);

  useEffect(() => {
    fetchMembers();
  }, [token]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/team-members?populate=*&sort=order:asc`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan teamleden niet laden');
      const data = await response.json();
      setMembers(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const normalizeOrientation = (file) =>
    new Promise((resolve) => {
      createImageBitmap(file).then((bitmap) => {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        bitmap.close();
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name, { type: blob.type })),
          file.type || 'image/jpeg',
          0.92
        );
      });
    });

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const normalized = await Promise.all(files.map(normalizeOrientation));
    const previews = normalized.map((f) => URL.createObjectURL(f));
    setNewFiles((prev) => [...prev, ...normalized]);
    setNewFilePreviews((prev) => [...prev, ...previews]);
    e.target.value = '';
  };

  const removeNewFile = (index) => {
    URL.revokeObjectURL(newFilePreviews[index]);
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setNewFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
  };

  const resetForm = () => {
    newFilePreviews.forEach((url) => URL.revokeObjectURL(url));
    setFormData({ name: '', role: '', bio: '', order: '' });
    setExistingImages([]);
    setNewFiles([]);
    setNewFilePreviews([]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId
        ? `${API_URL}/api/team-members/${editingId}`
        : `${API_URL}/api/team-members`;

      const bioBlocks = formData.bio
        ? [{ type: 'paragraph', children: [{ type: 'text', text: formData.bio }] }]
        : [];

      const memberRes = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          data: {
            name: formData.name,
            role: formData.role,
            bio: bioBlocks,
            order: formData.order !== '' ? parseInt(formData.order, 10) : 0,
          },
        }),
      });
      if (!memberRes.ok) {
        const err = await memberRes.json();
        throw new Error(err.message || 'Kan teamlid niet opslaan');
      }
      const memberData = await memberRes.json();
      const memberId = memberData.data.id;

      let newImageIds = [];
      if (newFiles.length > 0) {
        const uploadData = new FormData();
        newFiles.forEach((f) => uploadData.append('files', f));
        const uploadRes = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: uploadData,
        });
        if (!uploadRes.ok) throw new Error('Afbeelding uploaden mislukt');
        const uploaded = await uploadRes.json();
        newImageIds = uploaded.map((f) => f.id);
      }

      const keptIds = existingImages.map((img) => img.id);
      const finalIds = [...keptIds, ...newImageIds];

      await fetch(`${API_URL}/api/team-members/${memberId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data: { image: finalIds } }),
      });

      await fetchMembers();
      resetForm();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Weet je zeker dat je dit teamlid wilt verwijderen?')) return;
    try {
      const response = await fetch(`${API_URL}/api/team-members/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan teamlid niet verwijderen');
      await fetchMembers();
    } catch (err) {
      setError(err.message);
    }
  };

  const getBioText = (bio) => {
    if (!bio) return '';
    if (typeof bio === 'string') return bio;
    if (Array.isArray(bio)) {
      return bio
        .map((block) =>
          block.children ? block.children.map((c) => c.text || '').join(' ') : ''
        )
        .join(' ');
    }
    return '';
  };

  const handleEdit = (member) => {
    const attrs = member.attributes || member;
    const images = attrs.image?.data || attrs.image || [];
    const mapped = Array.isArray(images)
      ? images.map((img) => ({
          id: img.id,
          url: `${API_URL}${img.attributes?.url || img.url}`,
          name: img.attributes?.name || img.name || '',
        }))
      : [];

    setFormData({
      name: attrs.name || '',
      role: attrs.role || '',
      bio: getBioText(attrs.bio),
      order: attrs.order ?? '',
    });
    setExistingImages(mapped);
    setNewFiles([]);
    setNewFilePreviews([]);
    setEditingId(member.id);
    setShowForm(true);
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Team</h1>
        <button className="btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? 'Annuleren' : '+ Teamlid Toevoegen'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Naam</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Functie</label>
            <input
              type="text"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              rows="4"
              required
            />
          </div>
          <div className="form-group">
            <label>Volgorde</label>
            <input
              type="number"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: e.target.value })}
              placeholder="1, 2, 3…"
              required
            />
          </div>

          <div className="form-group">
            <label>Foto</label>

            {existingImages.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '8px' }}>
                  Huidige foto:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {existingImages.map((img) => (
                    <div key={img.id} style={{ position: 'relative' }}>
                      <img
                        src={img.url}
                        alt={img.name}
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', display: 'block' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(img.id)}
                        style={{
                          position: 'absolute', top: '-6px', right: '-6px',
                          width: '20px', height: '20px', borderRadius: '50%',
                          background: '#e53e3e', color: '#fff', border: 'none',
                          cursor: 'pointer', fontSize: '12px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {newFilePreviews.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '8px' }}>Nieuwe foto:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {newFilePreviews.map((src, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img
                        src={src}
                        alt=""
                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', display: 'block' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeNewFile(i)}
                        style={{
                          position: 'absolute', top: '-6px', right: '-6px',
                          width: '20px', height: '20px', borderRadius: '50%',
                          background: '#e53e3e', color: '#fff', border: 'none',
                          cursor: 'pointer', fontSize: '12px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <input type="file" accept="image/*" onChange={handleFileChange} />
          </div>

          <button type="submit" className="btn-submit" disabled={saving}>
            {saving ? 'Bezig met opslaan...' : editingId ? 'Teamlid Bijwerken' : 'Teamlid Aanmaken'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="loading">Team wordt geladen...</p>
      ) : members.length === 0 ? (
        <p className="empty">Geen teamleden gevonden</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Foto</th>
                <th>Naam</th>
                <th>Functie</th>
                <th>#</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const attrs = member.attributes || member;
                const images = attrs.image?.data || attrs.image || [];
                const thumb = Array.isArray(images) && images[0]
                  ? `${API_URL}${images[0].attributes?.url || images[0].url}`
                  : null;
                return (
                  <tr key={member.id}>
                    <td>
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={attrs.name}
                          style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '50%' }}
                        />
                      ) : (
                        <span style={{ color: '#aaa', fontSize: '12px' }}>Geen</span>
                      )}
                    </td>
                    <td>{attrs.name || ''}</td>
                    <td>{attrs.role || ''}</td>
                    <td>{attrs.order ?? '-'}</td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(member)}>
                        Bewerken
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(member.id)}>
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
